import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, FormSection, Field, SelectField, Button,
  LoadingState, EmptyState,
} from "components/ui";

const AdjustmentForm = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const branch = user?.branch;

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ productId: "", quantityChange: "", reason: "Damaged", note: "" });
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!branch) return;
    (async () => {
      try {
        const res = await axiosPrivate.get(`/api/branches/${branch.id}/stock`);
        setProducts(res.data);
      } catch (_) {
        toast.error("Failed to load products");
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, [branch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productId || form.quantityChange === "") {
      toast.warn("Please select a product and enter a quantity change.");
      return;
    }
    setSubmitting(true);
    try {
      await axiosPrivate.post("/api/stock/adjust", {
        productId: form.productId,
        quantityChange: parseFloat(form.quantityChange),
        reason: form.reason,
        note: form.note,
      });
      toast.success("Stock adjusted successfully");
      setForm({ productId: "", quantityChange: "", reason: "Damaged", note: "" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Adjustment failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!branch) {
    return (
      <PageContainer narrow>
        <PremiumCard>
          <EmptyState title="No branch assigned" />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer narrow>
      <PremiumCard title="Stock adjustment" subtitle="Add or remove inventory with a reason">
        {loadingProducts ? (
          <LoadingState />
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gap: 24 }}>
              <FormSection title="Product">
                <SelectField
                  label="Product" required
                  value={form.productId} onChange={change("productId")}
                >
                  <option value="">— Select product —</option>
                  {products.map((s) => (
                    <option key={s.id} value={s.product.id}>
                      {s.product.name} ({s.product.sku || "N/A"}) — Qty: {s.quantity}
                    </option>
                  ))}
                </SelectField>
              </FormSection>

              <FormSection title="Adjustment" columns={2}>
                <Field
                  label="Quantity change" required type="number" step="0.001"
                  value={form.quantityChange} onChange={change("quantityChange")}
                  placeholder="e.g. -2.5 or +3"
                  hint="+ to add, − to remove"
                />
                <SelectField label="Reason" required value={form.reason} onChange={change("reason")}>
                  <option value="Damaged">Damaged</option>
                  <option value="Expired">Expired</option>
                  <option value="Inventory Count">Inventory count</option>
                  <option value="Other">Other</option>
                </SelectField>
                <div style={{ gridColumn: "1 / -1" }}>
                  <Field label="Note" as="textarea" rows={3} value={form.note} onChange={change("note")} placeholder="Additional details (optional)…" />
                </div>
              </FormSection>
            </div>

            <div className="form-actionbar">
              <Button type="submit" variant="primary" loading={submitting}>Submit adjustment</Button>
            </div>
          </form>
        )}
      </PremiumCard>
    </PageContainer>
  );
};

export default AdjustmentForm;
